import { inject, Injectable } from '@angular/core';
import {
    IExternalLink,
    IExternalLinksCategory,
    IExternalLinksCategoryType,
    NormalizedLink,
    NormalizedLinkGroup,
    NormalizedLinkSection,
} from '@interacta-shared/feature-external-links';
import {
    DigitalWorkplaceDefinition,
    UsefulLinkWidget,
} from '@modules/digital-workplace/models/digital-workplace.model';
import { isUsefulLinkWidget } from '@modules/digital-workplace/models/digital-workplace.utils';
import { StateService } from '@modules/state/services/state.service';
import { UsefulLink } from '@modules/useful-link/models/useful-link.model';
import { TranslateService } from '@ngx-translate/core';

const MAIN_CATEGORY_TYPES = [
    IExternalLinksCategoryType.GUIDE_CENTER,
    IExternalLinksCategoryType.XLEARNING,
    IExternalLinksCategoryType.CHAT,
    IExternalLinksCategoryType.ADMINISTRATION,
    IExternalLinksCategoryType.SINGLE_LINK,
];

@Injectable({
    providedIn: 'root',
})
export class LinksDialogStateNormalizeService {
    private readonly stateService = inject(StateService);
    private readonly translateService = inject(TranslateService);

    readonly state = this.stateService.linksDialogState;

    updateNormalizedSections(): void {
        const extCategories = this.state.externalLinksCategories();
        const dwDefs = this.state.dwDefinitions();

        const normalizedSections = this.normalizeAllLinks(
            extCategories,
            dwDefs,
        );

        this.state.normalizedSections.set(
            normalizedSections.length > 0 ? normalizedSections : undefined,
        );
    }

    private normalizeAllLinks(
        externalLinksCategories: IExternalLinksCategory[] | null | undefined,
        dwDefinitions: DigitalWorkplaceDefinition[] | undefined,
    ): NormalizedLinkSection[] {
        const sections: NormalizedLinkSection[] = [];

        if (externalLinksCategories?.length) {
            const externalSections = this.processExternalLinksCategories(
                externalLinksCategories,
            );
            sections.push(...externalSections);
        }

        // Each DW definition becomes a section, each widget within the definition becomes a group
        if (dwDefinitions?.length) {
            dwDefinitions.forEach((dwDef) => {
                const usefulLinkWidgets = dwDef.widgets.filter(
                    isUsefulLinkWidget,
                ) as UsefulLinkWidget[];

                if (usefulLinkWidgets.length > 0) {
                    const groups: NormalizedLinkGroup[] = usefulLinkWidgets.map(
                        (widget) => this.normalizeUsefulLinkWidget(widget),
                    );

                    sections.push({
                        id: `dw-${dwDef.id}`,
                        label: dwDef.name.label ?? '',
                        groups,
                    });
                }
            });
        }

        return sections;
    }

    private processExternalLinksCategories(
        categories: IExternalLinksCategory[],
    ): NormalizedLinkSection[] {
        const sections: NormalizedLinkSection[] = [];

        // PINNED LINKS
        const pinnedLinks = categories
            .flatMap((cat) => cat.links)
            .filter((link) => link.pinned);
        if (pinnedLinks.length > 0) {
            sections.push({
                id: 'ext-section-pinned',
                label: this.translateService.instant('USEFUL_LINK.PINNED'),
                groups: [
                    {
                        id: 'ext-group-pinned',
                        links: pinnedLinks.map((link) =>
                            this.normalizeExternalLink(link),
                        ),
                    },
                ],
            });
        }

        // MAIN LINKS
        const mainLinks = categories
            .filter((cat) => MAIN_CATEGORY_TYPES.includes(cat.type))
            .flatMap((cat) => cat.links);
        if (mainLinks.length > 0) {
            sections.push({
                id: 'ext-section-main',
                label: this.translateService.instant(
                    'DIGITAL_WORKPLACE.EXTERNAL_LINKS',
                ),
                testId: 'useful-links-section-external',
                groups: [
                    {
                        id: 'ext-group-main',
                        links: mainLinks.map((link) =>
                            this.normalizeExternalLink(link),
                        ),
                    },
                ],
            });
        }

        // OTHER LINKS CATEGORIES
        const multipleLinkCategories = categories.filter(
            (cat) => cat.type === IExternalLinksCategoryType.MULTIPLE_LINK,
        );
        multipleLinkCategories.forEach((category) => {
            sections.push({
                id: `ext-section-${category.id}`,
                label: category.label,
                groups: [
                    {
                        id: `ext-group-${category.id}`,
                        links: category.links.map((link) =>
                            this.normalizeExternalLink(link),
                        ),
                    },
                ],
            });
        });

        return sections;
    }

    private normalizeUsefulLinkWidget(
        widget: UsefulLinkWidget,
    ): NormalizedLinkGroup {
        const normalizedLinks: NormalizedLink[] = [];

        widget.linksGroups.forEach((linksGroup, groupIdx) => {
            if (linksGroup.links.length === 1) {
                const link = linksGroup.links[0];
                normalizedLinks.push(this.normalizeUsefulLink(link, groupIdx));
            } else if (linksGroup.links.length > 1) {
                const groupedLink: NormalizedLink = {
                    id:
                        linksGroup.uuid ??
                        `useful-group-${widget.id}-${groupIdx}`,
                    label: linksGroup.name.label ?? '',
                    linkType: 'useful',
                    groupedLinks: linksGroup.links.map((link, linkIdx) =>
                        this.normalizeUsefulLink(link, linkIdx),
                    ),
                };
                normalizedLinks.push(groupedLink);
            }
        });

        return {
            id: `useful-widget-${widget.id}`,
            label: widget.name.label ?? '',
            links: normalizedLinks,
        };
    }

    private normalizeExternalLink(link: IExternalLink): NormalizedLink {
        return {
            id: link.id,
            label: link.label,
            url: link.url,
            imageUrl: link.imageUrl,
            colorCode: link.colorCode,
            pinned: link.pinned,
            authRequired: link.authRequired,
            linkType: 'external',
        };
    }

    private normalizeUsefulLink(
        link: UsefulLink,
        index: number,
    ): NormalizedLink {
        return {
            id: link.uuid ?? `useful-${index}`,
            label: link.name.label ?? '',
            url: link.url,
            communityId: link.communityId,
            homeId: link.homeId,
            imageUrl:
                link.temporaryContentPreviewImageLink ??
                link.temporaryContentViewLink,
            linkType: 'useful',
        };
    }
}
